import http from 'apis/config/http-service';
import { useMutationWrapper } from 'apis/config/use-api-wrapper';
import type { AxiosError } from 'axios';
import { useCustomToast } from 'design-system';
import { UseMutationOptions, useQueryClient } from 'react-query';
import type { AttributeValueAdmin } from './types';
import { productAttributesEndpoint } from './use-get-attribute-dictionary-api';

export interface CreateAttributeValuePayload {
  value: string;
  label: string;
  synonyms?: string[];
  sortOrder?: number;
  isActive?: boolean;
}

interface Params {
  typeId: number;
  data: CreateAttributeValuePayload;
}

type TData = AttributeValueAdmin;
type TError = AxiosError;
type TVariables = Params;

type Options = Omit<UseMutationOptions<TData, TError, TVariables>, 'mutationFn'>;
type TReturnType = ReturnType<typeof useMutationWrapper<TData, TError, TVariables>>;

// POST api/v1/cms/master-product-attributes/types/:typeId/values
export function useCreateAttributeValueApi(options?: Options): TReturnType {
  const queryClient = useQueryClient();
  const toast = useCustomToast();
  const { onSuccess: userOnSuccess, ...restOptions } = options || {};

  const mutationFn = async ({ typeId, data }: TVariables): Promise<TData> => {
    try {
      const response = await http.post(`${productAttributesEndpoint}/types/${typeId}/values`, data);
      return response.data.data;
    } catch (error: any) {
      if (!error.response?.status || !error.response?.data?.message) throw error;

      toast({
        description: error.response.data.message,
        status: 'error',
      });

      throw error;
    }
  };

  return useMutationWrapper(mutationFn, {
    mutationKey: [`${productAttributesEndpoint}/values`],
    onSuccess: (data, variables, context) => {
      queryClient.invalidateQueries([`${productAttributesEndpoint}/types`]);
      queryClient.invalidateQueries([`${productAttributesEndpoint}/dictionary`]);
      userOnSuccess?.(data, variables, context);
    },
    ...restOptions,
  });
}
